import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Edit, Delete, Search, Close } from "@mui/icons-material";
import AppTheme from "../components/shared-theme/AppTheme";
import { useSkills } from "../hooks/useSkills";
import Skill from "../types/Skill";

export default function DefinitionsSkills(props: {
  disableCustomTheme?: boolean;
}) {
  const { skills, loading, error, addSkill, updateSkill, deleteSkill } =
    useSkills();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill>({
    skillId: "",
    name: "",
    type: "",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState<Skill | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    if (error) {
      setSnackbarOpen(true);
    }
  }, [error]);

  const filteredSorted = React.useMemo(() => {
    const f = skills.filter((s) =>
      s.name.toLowerCase().includes(search.toLowerCase())
    );
    return f
      .slice()
      .sort((a, b) =>
        order === "asc"
          ? a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
          : b.name.localeCompare(a.name, undefined, { sensitivity: "base" })
      );
  }, [skills, search, order]);

  const handleOpenDialog = (skill?: Skill) => {
    setEditingSkill(skill || { skillId: "", name: "", type: "" });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSkill({ skillId: "", name: "", type: "" });
  };

  const handleSave = async () => {
    if (editingSkill.skillId) {
      await updateSkill(
        editingSkill.skillId,
        editingSkill.name,
        editingSkill.type
      );
    } else {
      await addSkill(editingSkill.name, editingSkill.type);
    }
    handleCloseDialog();
  };

  const confirmDelete = (skill: Skill) => {
    setSkillToDelete(skill);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (skillToDelete) {
      const success = await deleteSkill(skillToDelete.skillId);
      if (!success) {
        setSnackbarOpen(true);
      }
      setDeleteDialogOpen(false);
      setSkillToDelete(null);
    }
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box p={4}>
        <Card>
          <CardContent>
            <Box sx={{ mb: 2.5 }}>
              {/* header row */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{
                  pb: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  component="h2"
                  variant="overline"
                  sx={{
                    color: "text.secondary",
                    letterSpacing: 0.8,
                    fontWeight: 700,
                  }}
                >
                  Skills
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog()}
                >
                  Add Skill
                </Button>
              </Stack>
              {/* search row */}
              <TextField
                size="small"
                placeholder="Search skills…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mt: 2, width: "100%" }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearch("")}
                          aria-label="Clear"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  },
                }}
              />
            </Box>

            {loading ? (
              <Box
                sx={{
                  height: "350px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : skills.length === 0 ? (
              <Box
                sx={{
                  height: "350px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography align="center" color="textSecondary">
                  No skills available.
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active
                        direction={order}
                        onClick={() =>
                          setOrder((o) => (o === "asc" ? "desc" : "asc"))
                        }
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSorted.map((pos) => (
                    <TableRow key={pos.skillId}>
                      <TableCell>{pos.name}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          sx={{ marginRight: "5px" }}
                          onClick={() => handleOpenDialog(pos)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => confirmDelete(pos)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add / Edit Skill Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          slotProps={{
            paper: {
              component: "form",
              onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                handleCloseDialog();
              },
              sx: {
                backgroundImage: "none",
                width: "400px",
                maxWidth: "100%",
              },
            },
          }}
        >
          <DialogTitle>
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                letterSpacing: 0.8,
                fontWeight: 700,
              }}
            >
              {editingSkill.skillId ? "Edit Skill" : "Add Skill"}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth>
              <FormLabel htmlFor="skillNameId">Skill name</FormLabel>
              <TextField
                id="skillNameId"
                autoFocus
                placeholder="Skill name"
                value={editingSkill.name}
                onChange={(e) =>
                  setEditingSkill({
                    ...editingSkill,
                    name: e.target.value,
                  })
                }
              />
            </FormControl>

            <FormControl fullWidth>
              <FormLabel htmlFor="skillTypeId">Skill Type</FormLabel>
              <Select
                id="skillTypeId"
                value={editingSkill.type}
                onChange={(e) =>
                  setEditingSkill({
                    ...editingSkill,
                    type: e.target.value as Skill["type"],
                  })
                }
                displayEmpty
                renderValue={(selected) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ opacity: 0.6 }}>Select a type</span>
                  )
                }
                sx={{
                  background: "#fff",
                }}
              >
                <MenuItem value="backend">Backend</MenuItem>
                <MenuItem value="frontend">Frontend</MenuItem>
                <MenuItem value="devOps">DevOps</MenuItem>
                <MenuItem value="cloud">Cloud</MenuItem>
                <MenuItem value="database">Database</MenuItem>
                <MenuItem value="tools">Tools</MenuItem>
                <MenuItem value="others">Others</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
        {/* Delete Skill dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete{" "}
              <strong>{skillToDelete?.name}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="warning"
            onClose={() => setSnackbarOpen(false)}
            sx={{ width: "100%" }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </AppTheme>
  );
}
